import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * Scroll-fade wrapper — fades the content INTO the background colour at the
 * top/bottom edge of a vertical scroll area, but only while there is hidden
 * content in that direction (a cue that you are mid-scroll : content dissolves
 * where more of it continues off-screen).
 *
 * Why a JSX primitive (config-first escape hatch, justified) : this can't be
 * expressed in YAML/CSS. A pure-CSS `background-attachment: local` scroll shadow
 * paints on the container BACKGROUND, so it renders BEHIND opaque cards and only
 * peeks through the gaps. A static mask/overlay renders on top but can't tell
 * scrolled-from-unscrolled (it would fade the very first/last item when there is
 * nothing hidden). A fade *conditioned on scroll position* needs to read
 * scrollTop / clientHeight / scrollHeight — hence this small wrapper. It is
 * generic (no CRM/contact coupling) and reused by every scroll region.
 *
 * The overlays are `sticky` (pinned to the viewport edges), zero-layout-height
 * (negative margin), `pointer-events-none`, above the content (z-10), and paint
 * a `fadeRgb → transparent` gradient (so content melts into the container's own
 * background). Their opacity is toggled from the scroll state. Recompute fires
 * on scroll, on element resize (ResizeObserver) and on every render (covers
 * async content that grows the scrollHeight after mount, e.g. a decoupled
 * collection resolving).
 *
 * Props:
 *   - fadeRgb: space-separated RGB channels of the background the content should
 *     fade into. Default "248 250 252" (slate-50, the page). Use "255 255 255"
 *     inside a white card.
 *   - fadeSize: CSS height of each fade band. Default "2.5rem".
 *
 * YAML usage (drop-in for an `overflow-y-auto` div ; keep item spacing on an
 * INNER div so the sticky overlays aren't pushed by `space-y-*`) :
 *   - type: ScrollShadow
 *     fadeRgb: "255 255 255"
 *     attributes: { class: "max-h-96 overflow-y-auto" }
 *     content:
 *       - type: div
 *         attributes: { class: "space-y-2" }
 *         content: [ ... ]
 *
 * Registered in [App.jsx](../../../App.jsx) under `customPlugins.element.ScrollShadow`.
 */
export const ScrollShadow = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const wrapperClass = resolve(props.attributes?.class) ?? ''
  const fadeRgb = resolve(props.fadeRgb) || '248 250 252'
  const fadeSize = resolve(props.fadeSize) || '2.5rem'
  // Opt out of an edge when something else already owns it — e.g. a sticky
  // footer (pagination) pinned at the bottom makes the bottom fade both
  // redundant and misleading ("more content below" when there is none).
  const noTop = resolve(props.noTop) === true || resolve(props.noTop) === 'true'
  const noBottom = resolve(props.noBottom) === true || resolve(props.noBottom) === 'true'
  const ref = useRef(null)
  const [edges, setEdges] = useState({ top: false, bottom: false })
  // Effective fade band cap (px). A band is a real in-flow box of `fadeSize`
  // height (its negative margin only cancels its FLOW contribution, not the
  // painted box). When the content is shorter than a band, that box protrudes
  // and makes the region scroll-by-a-sliver — a phantom scroll that lights up
  // the "there is more below" fade on an empty/one-line list. Capping each band
  // to half the visible height guarantees band ≤ content, so no protrusion, no
  // phantom scroll. For a genuinely tall region the cap (½·clientHeight) far
  // exceeds fadeSize, so `min()` leaves the normal 2.5rem untouched.
  const [maxFadePx, setMaxFadePx] = useState(null)

  const recompute = useCallback(() => {
    const el = ref.current
    if (!el) return
    const top = el.scrollTop > 1
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1
    setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }))
    const cap = el.clientHeight / 2
    setMaxFadePx((prev) => (prev === cap ? prev : cap))
  }, [])

  // Element resize (e.g. viewport / max-height changes).
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(recompute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [recompute])

  // Every render : covers async content that grows scrollHeight after mount.
  // `recompute` only setState()s on an actual change, so this can't loop.
  useEffect(() => { recompute() })

  const base = 'sticky left-0 right-0 z-10 pointer-events-none transition-opacity duration-150'
  // Band height = fadeSize, but never taller than half the visible area (see
  // maxFadePx). The negative margin must track the SAME value so the band stays
  // flow-neutral.
  const bandHeight = maxFadePx == null ? fadeSize : `min(${fadeSize}, ${maxFadePx}px)`
  const topStyle = {
    height: bandHeight,
    marginBottom: `calc(-1 * (${bandHeight}))`,
    backgroundImage: `linear-gradient(to bottom, rgb(${fadeRgb}) 0%, rgb(${fadeRgb} / 0) 100%)`,
  }
  const bottomStyle = {
    height: bandHeight,
    marginTop: `calc(-1 * (${bandHeight}))`,
    backgroundImage: `linear-gradient(to top, rgb(${fadeRgb}) 0%, rgb(${fadeRgb} / 0) 100%)`,
  }

  return (
    <ActionDependant {...props}>
      <div ref={ref} onScroll={recompute} className={`relative ${wrapperClass}`}>
        {!noTop && (
          <div aria-hidden="true" style={topStyle} className={`${base} top-0 ${edges.top ? 'opacity-100' : 'opacity-0'}`} />
        )}
        {props.content != null && (
          <View props={props.content} path={`${path}.content`} datafield="content" currentData={currentData?.content} />
        )}
        {!noBottom && (
          <div aria-hidden="true" style={bottomStyle} className={`${base} bottom-0 ${edges.bottom ? 'opacity-100' : 'opacity-0'}`} />
        )}
      </div>
    </ActionDependant>
  )
}
